export const TOURS = {
  calendar: [
    {
      title: "Calendar Overview",
      body: "This page is your daily operations center. Start here to check pacing and jump into specific lesson days.",
    },
    {
      selector: '[data-tour="course-switcher"]',
      title: "Course Selector",
      body: "Pick the course first. Every page in the app updates based on this selection.",
    },
    {
      selector: '[data-tour="top-nav"]',
      title: "Main Navigation",
      body: "Use these tabs to move between Calendar, Guide, Units, Planner, TEKS, Tasks, Kiosk Control, and Admin.",
    },
    {
      selector: '[data-tour="calendar-grid"]',
      title: "Scope and Sequence Calendar",
      body: "Click any instructional day to open the linked lesson workspace.",
    },
    {
      selector: '[data-tour="calendar-legend"]',
      title: "Calendar Legend",
      body: "Use the legend to quickly identify regular days, CBA windows, and holidays/breaks.",
    },
    {
      selector: '[data-tour="calendar-resource-snap"]',
      title: "Snap Resource",
      body: "Save personal resources for the selected lesson day so planning artifacts stay connected to instruction.",
    },
  ],
  units: [
    {
      title: "Units View",
      body: "This page gives a high-level pacing and coverage view for experienced teachers.",
    },
    {
      selector: '[data-tour="units-kpis"]',
      title: "Unit KPIs",
      body: "Track lesson volume, instructional days, and CBA milestone density at a glance.",
    },
    {
      selector: '[data-tour="units-year-glance"]',
      title: "Year-at-a-Glance",
      body: "Review chapter pacing blocks and assessment windows aligned to scope and sequence.",
    },
    {
      selector: '[data-tour="units-list"]',
      title: "Unit Cards",
      body: "Expand a unit card to inspect chapters, TEKS coverage, milestone counts, and lesson previews.",
    },
    {
      selector: '[data-tour="units-open-guide"]',
      title: "Guide Shortcut",
      body: "Jump directly into guide-style views for the selected unit.",
    },
  ],
  planner: [
    {
      title: "Weekly Planner",
      body: "Use this page to assemble a full week: lessons, objectives, TEKS, notes, and resources.",
    },
    {
      selector: '[data-tour="planner-week-nav"]',
      title: "Week Navigation",
      body: "Move across weeks or return to the current week to plan around your actual teaching calendar.",
    },
    {
      selector: '[data-tour="planner-kpis"]',
      title: "Weekly Snapshot",
      body: "See planned days, TEKS coverage, milestones, and resource count before finalizing your week.",
    },
    {
      selector: '[data-tour="planner-grid"]',
      title: "Daily Planning Cards",
      body: "Each weekday card shows lesson assignment, TEKS, objective fields, and resources.",
    },
    {
      selector: '[data-tour="planner-teks-chip"]',
      title: "Clickable TEKS",
      body: "Click a TEKS chip to open standard details and alignment context while planning.",
    },
    {
      selector: '[data-tour="planner-copy"]',
      title: "Copy Weekly Plan",
      body: "Export the week summary to clipboard for PLC docs, email, or lesson plan templates.",
    },
  ],
  "kiosk-control": [
    {
      title: "Kiosk Control",
      body: "Use this page to publish a clean learning-objective display for classroom screens.",
    },
    {
      selector: '[data-tour="kiosk-control-week-nav"]',
      title: "Week Navigation",
      body: "Move across weeks to find the exact instructional day you want to broadcast.",
    },
    {
      selector: '[data-tour="kiosk-control-day-list"]',
      title: "Day Selector",
      body: "Choose a day to load lesson-linked TEKS and objective content for editing.",
    },
    {
      selector: '[data-tour="kiosk-control-editor"]',
      title: "Display Editor",
      body: "Edit the display title, TEKS code, objective statement, and I can statements for the selected day.",
    },
    {
      selector: '[data-tour="kiosk-control-entries"]',
      title: "Objective Blocks",
      body: "Add multiple objective blocks when you need to show more than one TEKS on the same display.",
    },
    {
      selector: '[data-tour="kiosk-control-header"]',
      title: "Publish and Open Display",
      body: "Publish sends current content to the kiosk screen. Open Kiosk Display launches the dedicated presentation page.",
    },
  ],
  "objective-library": [
    {
      title: "Objective Library",
      body: "This is your private objective workspace for teacher-created courses and reusable objective rows.",
    },
    {
      selector: '[data-tour="objective-library-create-course"]',
      title: "Create Objective Course",
      body: "Start by creating a private course. Your objective rows are grouped under these course shells.",
    },
    {
      selector: '[data-tour="objective-library-course-list"]',
      title: "My Objective Courses",
      body: "Select the course you want to work in. Course settings and objective fields load for the selected course.",
      missingTargetBody: "Create your first course to see this step.",
    },
    {
      selector: '[data-tour="objective-library-course-settings"]',
      title: "Selected Course Settings",
      body: "Update title, subject, grade, linked district course, or archive status for your selected private course.",
      missingTargetBody: "Select a course and click its edit icon to open course settings.",
    },
    {
      selector: '[data-tour="objective-library-entry-editor"]',
      title: "Add Objective Entry",
      body: "Add TEKS code, learning objective, and I can statements. Unit, lesson, date, full TEKS text, and notes are optional.",
      missingTargetBody: "Select a course, then click Add Course Objective to open this form.",
    },
    {
      selector: '[data-tour="objective-library-saved-entries"]',
      title: "Saved Entries",
      body: "Edit or delete saved objective rows here, and use filters to find previously saved items quickly.",
      missingTargetBody: "Create your first course to see this step.",
    },
    {
      selector: '[data-tour="objective-library-bulk-toggle"]',
      title: "Bulk Upload Tools",
      body: "Open this section to download template CSV files, validate uploads, and import multiple rows at once.",
    },
  ],
  guide: [
    {
      title: "Guide Page",
      body: "This page mirrors district guide flow and lets teachers navigate sections quickly.",
    },
    {
      selector: '[data-tour="guide-unit-select"]',
      title: "Unit Selector",
      body: "Switch unit guides without leaving the page.",
    },
    {
      selector: '[data-tour="guide-sidebar"]',
      title: "Section Navigation",
      body: "Use the left navigation to jump to pacing sections, vocabulary, and lesson sequence.",
    },
    {
      selector: '[data-tour="guide-hero"]',
      title: "Guide Header",
      body: "Reference date windows, total days, and source context for the current unit.",
    },
    {
      selector: '[data-tour="guide-vocab"]',
      title: "Vocabulary and Clarifications",
      body: "Review pedagogy-driven vocabulary lists and misconception clarifications.",
    },
    {
      selector: '[data-tour="guide-lesson-sequence"]',
      title: "Lesson Sequence",
      body: "Browse chapter lessons and click TEKS badges to inspect standard details.",
    },
  ],
  teks: [
    {
      title: "TEKS Library",
      body: "Use this page to review all standards tied to the selected course.",
    },
    {
      selector: '[data-tour="teks-list"]',
      title: "Standards List",
      body: "Each row shows TEKS code, full description, category, and CBA item weight.",
    },
    {
      selector: '[data-tour="teks-profile-btn"]',
      title: "TEKS Profile",
      body: "Open profile view to inspect details and vertical alignment.",
    },
  ],
  settings: [
    {
      title: "Admin Workspace",
      body: "Use this page to manage course setup and monitor data quality across the planner.",
    },
    {
      selector: '[data-tour="admin-overview"]',
      title: "System Overview",
      body: "Review totals for courses, units, lessons, TEKS, calendar rows, and district assets.",
    },
    {
      selector: '[data-tour="admin-category-summary"]',
      title: "Category Distribution",
      body: "Confirm the split across MS and HS categories for Math, RLA, Science, and Social Studies.",
    },
    {
      selector: '[data-tour="admin-refresh"]',
      title: "Refresh Data",
      body: "Refresh after running migrations or editing records so the calendar reflects current values.",
    },
    {
      selector: '[data-tour="admin-create-course"]',
      title: "Quick Add Course",
      body: "Add a new course with grade level and category before uploading TEKS and pacing migrations.",
    },
    {
      selector: '[data-tour="admin-category-manager"]',
      title: "Course Category Manager",
      body: "Edit course name, grade, and category in one table and save row-by-row or in bulk.",
    },
    {
      selector: '[data-tour="admin-filters"]',
      title: "Search and Filter",
      body: "Filter by category or search by name/grade to target the exact courses you need to update.",
    },
    {
      selector: '[data-tour="admin-bulk-tools"]',
      title: "Bulk Category Tools",
      body: "Select multiple courses and apply one category assignment to all selected rows.",
    },
    {
      selector: '[data-tour="admin-save-all"]',
      title: "Save All Changes",
      body: "Commit every unsaved row at once after reviewing edits.",
    },
  ],
  lesson: [
    {
      title: "Lesson Workspace",
      body: "This deep-dive page supports lesson execution and TEKS-aligned instructional decisions.",
    },
    {
      selector: '[data-tour="lesson-header"]',
      title: "Lesson Header",
      body: "Confirm lesson title, TEKS alignment, and chapter context before instruction.",
    },
    {
      selector: '[data-tour="lesson-tabs"]',
      title: "Lesson Tabs",
      body: "Switch between overview, formula bar, resources, and pedagogy guardrails.",
    },
    {
      selector: '[data-tour="lesson-tab-content"]',
      title: "Instructional Content",
      body: "Use this area for objectives, success criteria, formulas, and misconception checks.",
    },
    {
      selector: '[data-tour="lesson-close"]',
      title: "Return to Calendar",
      body: "Close the lesson workspace to return to the calendar and planning views.",
    },
  ],
};

export const getTourSteps = (routeName) => {
  if (routeName === "dashboard") return TOURS.calendar || [];
  return TOURS[routeName] || [];
};
